import app from '../../api/app';
import { attemptAction } from '../index';
import { clearUserInfo, setUserInfo } from '../../app/store';

import DOMPurify from 'dompurify';

// Detect portal from domain
const getPortal = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('readingplex')) return 'rp';
    return 'mp'; // default to MathPlex
};

// Detect if this is member portal
const isMemberPortal = () => {
    const hostname = window.location.hostname;
    // Member portal: must include 'member.' subdomain
    return hostname.includes('member.');
};

export const login = (email, password) => async (dispatch) => {
    // Clear in case any stupid old cookies came back to life
    await dispatch(clearUserInfo());

    const portal = getPortal();
    const authEndpoint = isMemberPortal() ? '/memberAuth/login' : '/adminAuth/login';
    console.log('[AUTH] Login attempt:', { email, portal, authEndpoint });

    try {
        const response = await app.post(authEndpoint, {
            email,
            password,
            portal
        });

        console.log('[AUTH] Response received:', {
            hasResponse: !!response,
            status: response?.status,
            statusText: response?.statusText,
            hasData: !!response?.data,
            dataKeys: response?.data ? Object.keys(response.data) : [],
            fullResponse: response
        });

        // Success - response has status and data
        if (response && response.status === 201) {
            console.log('[AUTH] Success - status 201, dispatching setUserInfo');
            dispatch(setUserInfo({
                refreshToken: response.data.rToken,
                accessToken: response.data.aToken,
                pubKey: response.data.pubKey,
            }));
            return response;
        }

        // Non-201 status - return as error
        console.log('[AUTH] Non-201 status:', response?.status);
        return response;
    } catch (e) {
        // Handle network errors and API errors
        console.error('[AUTH] Login error caught:', {
            hasResponse: !!e.response,
            hasRequest: !!e.request,
            message: e.message,
            responseStatus: e.response?.status,
            responseData: e.response?.data,
            fullError: e
        });

        if (e.response) {
            // API returned an error response (4xx, 5xx)
            console.log('[AUTH] Returning error response:', e.response);
            return e.response;
        } else if (e.request) {
            // Request was made but no response received (network error, CORS, timeout)
            console.error('[AUTH] Network error - request made but no response');
            return { status: 0, data: { message: 'Network error: Unable to reach the server. Please check your connection.' } };
        } else {
            // Something else happened (request setup error)
            console.error('[AUTH] Request setup error:', e.message);
            return { status: 0, data: { message: 'Unable to reach the server at this time.' } };
        }
    }
}

export const signout = () => async (dispatch) => {
    async function action(params) {
        let response = null;
        const aToken = params.aToken;
        const portal = getPortal();
        const authEndpoint = isMemberPortal() ? '/memberAuth/signout' : '/adminAuth/signout';
        try {
            response = await app.post(authEndpoint, {
                aToken,
                portal
            });
        } catch (e) {
            response = e.response;
            console.log(e);
        } finally {
            if (response) {
                if (response.status === 201) {
                    dispatch(clearUserInfo());
                    // dispatch ({ type: CLEAR_CONNECTIONS });
                    return response;
                } else {
                    return response;
                }
            } else {
                return {};
            }
        }
    }
    return await attemptAction(action, dispatch);
}

export const forceSignout = () => async (dispatch) => {
    async function action(params) {
        const aToken = params.aToken;
        const portal = getPortal();
        const authEndpoint = isMemberPortal() ? '/memberAuth/force-signout' : '/adminAuth/force-signout';
        let response = null;
        try {
            app.post(authEndpoint, {
                aToken,
                portal
            });
        } catch (e) {
            response = e.response;
            console.log(e);
        } finally {
            dispatch(clearUserInfo());
            // dispatch ({ type: CLEAR_CONNECTIONS });
            return response;
        }
    }
    return await attemptAction(action, dispatch);
}

export const requestPasswordReset = (email) => async () => {
    email = DOMPurify.sanitize(email);
    const portal = getPortal();
    const authEndpoint = isMemberPortal() ? '/memberAuth/request-password-reset' : '/adminAuth/request-password-reset';
    let response = null;
    try {
        response = await app.post(authEndpoint, {
            email,
            portal
        });
    } catch (e) {
        response = e.response;
        console.log(e);
    } finally {
        return response;
    }
}

export const resetPassword = (email, newPass, hash) => async () => {
    newPass = DOMPurify.sanitize(newPass);
    const portal = getPortal();
    const authEndpoint = isMemberPortal() ? '/memberAuth/reset-password' : '/adminAuth/reset-password';
    let response = null;
    try {
        response = await app.post(authEndpoint, {
            email,
            newPass,
            hash,
            portal
        });
    } catch (e) {
        response = e.response;
        console.log(e);
    } finally {
        return response;
    }
}

export const googleLogin = (googleCredential) => async (dispatch) => {
    // Clear previous session
    await dispatch(clearUserInfo());

    const portal = getPortal();
    const authEndpoint = isMemberPortal() ? '/memberAuth/google-login' : '/adminAuth/google-login';
    console.log('[AUTH] Google login attempt:', { portal, authEndpoint });

    try {
        const response = await app.post(authEndpoint, {
            credential: googleCredential,
            portal
        });

        console.log('[AUTH] Google response received:', {
            hasResponse: !!response,
            status: response?.status,
            hasData: !!response?.data
        });

        // Success
        if (response && response.status === 201) {
            console.log('[AUTH] Google login success - status 201');
            dispatch(setUserInfo({
                refreshToken: response.data.rToken,
                accessToken: response.data.aToken,
                pubKey: response.data.pubKey,
            }));
            return response;
        }

        console.log('[AUTH] Google login non-201 status:', response?.status);
        return response;
    } catch (e) {
        console.error('[AUTH] Google login error:', e);
        if (e.response) {
            return e.response;
        }
        return { status: 0, data: { message: 'Google login failed. Please try again.' } };
    }
}